import { getEvidenceDocumentRules } from '@/components/claims/newClaim/schema/evidenceDocumentRules';
import {
  ConditionalOperatorArray,
  type DocumentCategory,
  type DocumentInfo,
  type DocumentIsRequiredWhenCondition,
  type DocumentRowData,
  type DraftClaimAdditionalExpense,
  type DraftClaimCancellationExpense,
  type EvidenceDocumentRequirementRule,
  type RequiredDocumentsByCategory,
  type UserDraftClaim,
  type WatchedFormValues,
} from '@/types';
import {
  additionalExpenseLabels,
  cancellationExpenseLabels,
  medicalExpenseLabels,
} from '@data-shared/constants/expenseTypeLabels';
import { evaluateArrayCondition, evaluatePrimitiveCondition } from './form';

function evaluateCondition(
  condition: DocumentIsRequiredWhenCondition,
  watchedFormValues: WatchedFormValues,
) {
  const { field, value, operator } = condition;
  const watchedFieldValue = watchedFormValues[field];

  switch (operator) {
    case ConditionalOperatorArray.In:
    case ConditionalOperatorArray.NotIn:
    case ConditionalOperatorArray.Includes:
    case ConditionalOperatorArray.NotIncludes:
      return evaluateArrayCondition(operator, watchedFieldValue, value);
    default:
      return evaluatePrimitiveCondition(operator, watchedFieldValue, value);
  }
}

/**
 * Determines whether an evidence document is required, based on 'isRequiredWhen' conditions.
 * All conditions must pass (AND-ed together). A rule with no conditions is always required.
 */
function getIsEvidenceDocumentRequired(
  isRequiredWhen: EvidenceDocumentRequirementRule['isRequiredWhen'],
  watchedFormValues: WatchedFormValues,
) {
  if (!isRequiredWhen?.length) {
    return true;
  }

  return isRequiredWhen.every(condition => evaluateCondition(condition, watchedFormValues));
}

/**
 * Determines the distinct evidence documents required for a claim, given the document
 * requirement rules and the claimant's answers so far.
 */
function getRequiredEvidenceDocuments(allAnswers: WatchedFormValues): DocumentRowData[] {
  const requiredDocumentTypes = getEvidenceDocumentRules()
    .filter(({ isRequiredWhen }) => {
      return getIsEvidenceDocumentRequired(isRequiredWhen, allAnswers);
    })
    .map(({ documentType }) => documentType);

  // dedupe the entries
  return [...new Set(requiredDocumentTypes)].map(documentType => ({
    documentType,
  }));
}

interface GetRefundableExpenseRowsParams<
  T extends DraftClaimCancellationExpense | DraftClaimAdditionalExpense,
> {
  expenses: T[];
  expenseLabelResolver: (expenseType: T['expenseType']) => string;
}
function getRefundableExpenseRows<
  T extends DraftClaimCancellationExpense | DraftClaimAdditionalExpense,
>({ expenses, expenseLabelResolver }: GetRefundableExpenseRowsParams<T>) {
  return expenses.flatMap(
    ({
      expenseId,
      expenseType,
      expenseAmount,
      creditNoteOrRefundReceived,
      creditNoteOrRefundAmount,
    }) => [
      {
        documentType: expenseLabelResolver(expenseType),
        expenseId,
        amount: expenseAmount,
      },
      ...(creditNoteOrRefundReceived
        ? [
            {
              documentType: 'Refund or Credit note',
              expenseId,
              amount: creditNoteOrRefundAmount,
            },
          ]
        : []),
    ],
  );
}

function getRequiredExpenseDocuments(expenses: UserDraftClaim['expenses']): DocumentRowData[] {
  if (!expenses) {
    return [];
  }

  const medicalRows =
    expenses.medicalExpenses?.map(({ expenseId, expenseType, expenseAmount }) => ({
      documentType: expenseType ? medicalExpenseLabels[expenseType] : 'Unknown',
      expenseId,
      amount: expenseAmount,
    })) ?? [];

  return [
    ...medicalRows,
    ...getRefundableExpenseRows({
      expenses: expenses.cancellationExpenses ?? [],
      expenseLabelResolver: expenseType =>
        expenseType ? cancellationExpenseLabels[expenseType] : '',
    }),
    ...getRefundableExpenseRows({
      expenses: expenses.additionalExpenses ?? [],
      expenseLabelResolver: expenseType =>
        expenseType ? additionalExpenseLabels[expenseType] : '',
    }),
  ];
}

/**
 * Determines the distinct documents required for a claim, given the document
 * requirement rules and the claimant's answers so far. Results are grouped by
 * 'category', deduped by 'documentType' within each category.
 */
function getRequiredDocuments(
  allAnswers: WatchedFormValues,
  draftClaim?: UserDraftClaim,
): RequiredDocumentsByCategory {
  return {
    evidence: getRequiredEvidenceDocuments(allAnswers),
    expense: getRequiredExpenseDocuments(draftClaim?.expenses),
  };
}

/**
 * True when the user has uploaded a document for every required document type.
 */
function getHasCompleteRequiredDocuments(
  uploadedDocuments: DocumentInfo[],
  requiredDocuments: RequiredDocumentsByCategory,
): boolean {
  if (!Object.keys(requiredDocuments).length) {
    return true;
  }

  if (!uploadedDocuments.length) {
    return false;
  }

  const requiredKeys = Object.entries(requiredDocuments).flatMap(([category, documentRows]) =>
    documentRows.map(({ documentType, expenseId }) =>
      getDocumentRowKey({ category, documentType, expenseId }),
    ),
  );

  const uploadedKeys = new Set(
    uploadedDocuments.map(({ category, documentType, expenseId }) =>
      getDocumentRowKey({ category, documentType, expenseId }),
    ),
  );

  return requiredKeys.every(requiredKey => uploadedKeys.has(requiredKey));
}

interface FilterUploadedDocumentsParams {
  uploadedDocuments: DocumentInfo[];
  category: DocumentCategory;
  documentType: string;
  expenseId?: string;
}
function filterUploadedDocuments({
  uploadedDocuments,
  category,
  documentType,
  expenseId,
}: FilterUploadedDocumentsParams) {
  return uploadedDocuments
    .filter(document => document.category === category && document.documentType === documentType)
    .filter(
      document =>
        document.category !== 'expense' || (document.expenseId && document.expenseId === expenseId),
    );
}

/**
 * Returns a key string for the given information.
 * Used when we need to identify a document upload row, e.g. component key for DocumentTypeRow,
 * or for matching required documents with uploaded documents
 */
interface GetDocumentRowKeyParam {
  category: string;
  documentType: string;
  expenseId?: string;
}
function getDocumentRowKey({ category, documentType, expenseId }: GetDocumentRowKeyParam) {
  return `${category}_${documentType}` + (expenseId ? `_${expenseId}` : '');
}

export {
  filterUploadedDocuments,
  getDocumentRowKey,
  getHasCompleteRequiredDocuments,
  getRequiredDocuments,
};
